import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Typography,
  Stack,
  Container,
  Card,
  CardContent,
  Box,
  Grid,
  Button,
  Chip,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Select,
  Option,
  CircularProgress,
  Divider,
  Sheet,
  Table,
  SheetProps,
  IconButton,
  Tooltip as JoyTooltip,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Alert
} from '@mui/joy'
import {
  Api as ApiIcon,
  Code as CodeIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  Dashboard as DashboardIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  AutoAwesome as AutoAwesomeIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material'
import { toast } from 'sonner'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import api from '../services/api'
import ApiMessageSettingsPanel from '../components/ApiMessageSettingsPanel'
import ApiMessageGuide from '../components/ApiMessageGuide'

// Colores para gráficos
const COLORS = {
  success: '#4caf50',
  failed: '#f44336',
  sent: '#2196f3',
  primary: '#1976d2'
}

interface DashboardStats {
  totalAllTime: number
  daily: { sent: number; success: number; failed: number }
  weekly: { sent: number; success: number; failed: number }
  monthly: { sent: number; success: number; failed: number }
}

interface DetailedStats {
  period: string
  startDate: string
  endDate: string
  totals: {
    sent: number
    success: number
    failed: number
    text: number
    pdf: number
    image: number
    video: number
    other: number
    checkNumber: number
  }
  dailyStats: Array<{
    date: string
    sent: number
    success: number
    failed: number
  }>
}

interface FailedMessage {
  id: number
  companyId: number
  whatsappId: number | null
  number: string | null
  message: string | null
  error: string | null
  errorCode: string | null
  errorSubcode: string | null
  fbtraceId: string | null
  status: 'pending' | 'retried' | 'failed'
  retryCount: number
  ticketId: number | null
  endpoint: string
  metadata: any
  createdAt: string
  updatedAt: string
}

interface FailedMessagesPagination {
  total: number
  page: number
  limit: number
  pages: number
}

export default function ApiMessages() {
  const [searchParams] = useSearchParams()
  const initialTab = parseInt(searchParams.get("tab") || "0", 10)
  const [tabIndex, setTabIndex] = useState(initialTab)
  const [period, setPeriod] = useState<string>('week')
  const [loading, setLoading] = useState(true)
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null)
  const [detailedStats, setDetailedStats] = useState<DetailedStats | null>(null)

  // Failed Messages State
  const [failedMessages, setFailedMessages] = useState<FailedMessage[] | null>(null)
  const [failedLoading, setFailedLoading] = useState(false)
  const [retryingId, setRetryingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [selectedFailed, setSelectedFailed] = useState<FailedMessage | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [failedStatus, setFailedStatus] = useState<string>('all')
  const [failedEndpoint, setFailedEndpoint] = useState<string>('all')
  const [failedPage, setFailedPage] = useState(1)
  const [failedPagination, setFailedPagination] = useState<FailedMessagesPagination | null>(null)
  const [failedWhatsappId, setFailedWhatsappId] = useState<string>('all')

  // Conexiones de la empresa (para el filtro por conexión). Multi-tenant:
  // el endpoint solo devuelve las conexiones de la company del usuario.
  const [connections, setConnections] = useState<{ id: number; name: string }[]>([])

  useEffect(() => {
    fetchStats()
  }, [period])

  const fetchStats = async () => {
    setLoading(true)
    try {
      const [dashboardRes, statsRes] = await Promise.all([
        api.get('/api/messages/dashboard-stats'),
        api.get(`/api/messages/stats?period=${period}`)
      ])
      setDashboardStats(dashboardRes.data)
      setDetailedStats(statsRes.data)
    } catch (err) {
      console.error('Error fetching API stats:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchConnections = async () => {
    try {
      const res = await api.get('/api-message-settings')
      const records = (res.data?.records || []) as { id: number; name: string }[]
      setConnections(records.map(r => ({ id: r.id, name: r.name })))
    } catch (err) {
      console.error('Error fetching connections:', err)
    }
  }

  const fetchFailedMessages = async () => {
    setFailedLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(failedPage),
        limit: '20'
      })
      if (failedStatus !== 'all') params.set('status', failedStatus)
      if (failedEndpoint !== 'all') params.set('endpoint', failedEndpoint)
      if (failedWhatsappId !== 'all') params.set('whatsappId', failedWhatsappId)

      const res = await api.get(`/api/messages/failed-messages?${params.toString()}`)
      setFailedMessages(res.data.messages || [])
      setFailedPagination(res.data.pagination || null)
    } catch (err) {
      console.error('Error fetching failed messages:', err)
      toast.error('Error al cargar mensajes fallidos')
    } finally {
      setFailedLoading(false)
    }
  }

  const handleRetry = async (id: number) => {
    setRetryingId(id)
    try {
      await api.post(`/api/messages/failed-messages/${id}/retry`)
      toast.success('Mensaje reenviado correctamente')
      await fetchFailedMessages()
    } catch (err: any) {
      console.error('Error retrying message:', err)
      toast.error(err.response?.data?.message || 'Error al reintentar mensaje')
    } finally {
      setRetryingId(null)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este mensaje fallido?')) return

    setDeletingId(id)
    try {
      await api.delete(`/api/messages/failed-messages/${id}`)
      toast.success('Mensaje eliminado correctamente')
      await fetchFailedMessages()
    } catch (err: any) {
      console.error('Error deleting message:', err)
      toast.error(err.response?.data?.message || 'Error al eliminar mensaje')
    } finally {
      setDeletingId(null)
    }
  }

  const openDetails = (msg: FailedMessage) => {
    setSelectedFailed(msg)
    setDetailsOpen(true)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning'
      case 'retried': return 'success'
      case 'failed': return 'danger'
      default: return 'neutral'
    }
  }

  // Cargar conexiones una vez (para el filtro por conexión).
  useEffect(() => {
    fetchConnections()
  }, [])

  // Cargar mensajes fallidos cuando se cambie a la pestaña (índice 1 tras
  // eliminar la pestaña de Documentación).
  useEffect(() => {
    if (tabIndex === 1) {
      fetchFailedMessages()
    }
  }, [tabIndex, failedStatus, failedEndpoint, failedWhatsappId, failedPage])

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num?.toString() || '0'
  }

  const getPeriodLabel = (): string => {
    switch (period) {
      case 'day': return 'Hoy'
      case 'week': return 'Esta Semana'
      case 'month': return 'Este Mes'
      default: return ''
    }
  }

  const getCurrentPeriodStats = () => {
    if (!dashboardStats) return { sent: 0, success: 0, failed: 0 }
    switch (period) {
      case 'day': return dashboardStats.daily
      case 'week': return dashboardStats.weekly
      case 'month': return dashboardStats.monthly
      default: return dashboardStats.weekly
    }
  }

  const prepareChartData = () => {
    if (!detailedStats?.dailyStats) return []
    return detailedStats.dailyStats.map(day => ({
      date: day.date.substring(5), // MM-DD
      Exitosos: day.success,
      Fallidos: day.failed
    }))
  }

  const preparePieData = () => {
    const stats = getCurrentPeriodStats()
    return [
      { name: 'Exitosos', value: stats.success || 0 },
      { name: 'Fallidos', value: stats.failed || 0 }
    ]
  }

  const currentStats = getCurrentPeriodStats()
  const chartData = prepareChartData()
  const pieData = preparePieData()

  // Dashboard Tab Content
  const DashboardContent = () => (
    <Stack spacing={3}>
      {/* Filtro de período */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Select
          value={period}
          onChange={(_, value) => value && setPeriod(value)}
          size="sm"
          sx={{ minWidth: 150 }}
        >
          <Option value="day">Hoy</Option>
          <Option value="week">Ultima Semana</Option>
          <Option value="month">Ultimo Mes</Option>
        </Select>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Card Total - Grande arriba */}
          <Card
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
              color: 'white'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                    Total de Envios (Historico)
                  </Typography>
                  <Typography level="h1" sx={{ fontWeight: 700, color: 'white' }}>
                    {formatNumber(dashboardStats?.totalAllTime || 0)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    p: 2,
                    display: 'flex'
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Cards de estadísticas del período - 3 columnas */}
          <Grid container spacing={2}>
            <Grid xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography level="body-sm" sx={{ color: 'text.tertiary', textTransform: 'uppercase', fontWeight: 500 }}>
                        Enviados ({getPeriodLabel()})
                      </Typography>
                      <Typography level="h2" sx={{ color: COLORS.sent, fontWeight: 700 }}>
                        {formatNumber(currentStats.sent)}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'primary.softBg', borderRadius: '50%', p: 1.5 }}>
                      <SendIcon sx={{ color: COLORS.sent, fontSize: 30 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} md={4}>
              <Card sx={{ borderLeft: `4px solid ${COLORS.success}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography level="body-sm" sx={{ color: 'text.tertiary', textTransform: 'uppercase', fontWeight: 500 }}>
                        Exitosos ({getPeriodLabel()})
                      </Typography>
                      <Typography level="h2" sx={{ color: COLORS.success, fontWeight: 700 }}>
                        {formatNumber(currentStats.success)}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'success.softBg', borderRadius: '50%', p: 1.5 }}>
                      <CheckCircleIcon sx={{ color: COLORS.success, fontSize: 30 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} md={4}>
              <Card sx={{ borderLeft: `4px solid ${COLORS.failed}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography level="body-sm" sx={{ color: 'text.tertiary', textTransform: 'uppercase', fontWeight: 500 }}>
                        Fallidos ({getPeriodLabel()})
                      </Typography>
                      <Typography level="h2" sx={{ color: COLORS.failed, fontWeight: 700 }}>
                        {formatNumber(currentStats.failed)}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'danger.softBg', borderRadius: '50%', p: 1.5 }}>
                      <ErrorIcon sx={{ color: COLORS.failed, fontSize: 30 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Gráficos */}
          <Grid container spacing={2}>
            {/* Gráfico de barras */}
            <Grid xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 2, fontWeight: 600 }}>
                    Envios Diarios ({getPeriodLabel()})
                  </Typography>
                  <Box sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="Exitosos" fill={COLORS.success} />
                        <Bar dataKey="Fallidos" fill={COLORS.failed} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Gráfico de torta */}
            <Grid xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 2, fontWeight: 600 }}>
                    Distribucion ({getPeriodLabel()})
                  </Typography>
                  <Box sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(props: any) => `${props.name}: ${((props.percent || 0) * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={index === 0 ? COLORS.success : COLORS.failed} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Desglose por tipo de contenido */}
          {detailedStats && (
            <Card>
              <CardContent>
                <Typography level="title-lg" sx={{ mb: 2, fontWeight: 600 }}>
                  Desglose por Tipo de Contenido ({getPeriodLabel()})
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { label: 'Texto', value: detailedStats.totals?.text || 0 },
                    { label: 'Imagenes', value: detailedStats.totals?.image || 0 },
                    { label: 'PDF', value: detailedStats.totals?.pdf || 0 },
                    { label: 'Videos', value: detailedStats.totals?.video || 0 },
                    { label: 'Otros', value: detailedStats.totals?.other || 0 },
                    { label: 'Verificaciones', value: detailedStats.totals?.checkNumber || 0 }
                  ].map((item, index) => (
                    <Grid xs={6} sm={4} md={2} key={index}>
                      <Sheet
                        variant="soft"
                        sx={{ p: 2, borderRadius: 'md', textAlign: 'center' }}
                      >
                        <Typography level="h3" sx={{ color: 'primary.main' }}>
                          {item.value}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                          {item.label}
                        </Typography>
                      </Sheet>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Stack>
  )

  // Failed Messages Tab Content
  const FailedMessagesContent = () => {
  // Ensure failedMessages is always an array
  const messages = Array.isArray(failedMessages) ? failedMessages : []

  return (
    <Stack spacing={3}>
      {/* Header con botón de actualizar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography level="h4">
          Mensajes Fallidos
        </Typography>
        <Button
          size="sm"
          variant="outlined"
          onClick={fetchFailedMessages}
          startDecorator={<RefreshIcon />}
        >
          Actualizar
        </Button>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Select
          value={failedStatus}
          onChange={(_, value) => {
            if (!value) return
            setFailedStatus(value)
            setFailedPage(1)
          }}
          size="sm"
          sx={{ minWidth: 180 }}
        >
          <Option value="all">Todos los estados</Option>
          <Option value="pending">Pendientes</Option>
          <Option value="failed">Fallidos</Option>
          <Option value="retried">Reenviados</Option>
        </Select>
        <Select
          value={failedEndpoint}
          onChange={(_, value) => {
            if (!value) return
            setFailedEndpoint(value)
            setFailedPage(1)
          }}
          size="sm"
          sx={{ minWidth: 180 }}
        >
          <Option value="all">Todos los endpoints</Option>
          <Option value="send-template">Templates</Option>
          <Option value="send">Texto</Option>
          <Option value="send/linkImage">Imagen</Option>
        </Select>
        <Select
          value={failedWhatsappId}
          onChange={(_, value) => {
            if (!value) return
            setFailedWhatsappId(value)
            setFailedPage(1)
          }}
          size="sm"
          sx={{ minWidth: 200 }}
        >
          <Option value="all">Todas las conexiones</Option>
          {connections.map(conn => (
            <Option key={conn.id} value={String(conn.id)}>
              {conn.name}
            </Option>
          ))}
        </Select>
      </Stack>

      {failedLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : messages.length === 0 ? (
        <Alert color="success" variant="soft" startDecorator={<CheckCircleIcon />}>
          No hay mensajes fallidos. ¡Todos los envíos fueron exitosos!
        </Alert>
      ) : (
        <>
          {/* Resumen de estados */}
          <Grid container spacing={2}>
            <Grid xs={12} sm={4}>
              <Card sx={{ borderLeft: '4px solid #f59e0b' }}>
                <CardContent>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Pendientes
                  </Typography>
                  <Typography level="h3" sx={{ color: '#f59e0b' }}>
                    {messages.filter(m => m.status === 'pending').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid xs={12} sm={4}>
              <Card sx={{ borderLeft: '4px solid #4caf50' }}>
                <CardContent>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Reenviados
                  </Typography>
                  <Typography level="h3" sx={{ color: '#4caf50' }}>
                    {messages.filter(m => m.status === 'retried').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid xs={12} sm={4}>
              <Card sx={{ borderLeft: '4px solid #f44336' }}>
                <CardContent>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Fallidos Definitivos
                  </Typography>
                  <Typography level="h3" sx={{ color: '#f44336' }}>
                    {messages.filter(m => m.status === 'failed').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabla de mensajes fallidos */}
          <Sheet variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Mensaje</th>
                  <th>Error</th>
                  <th>Endpoint</th>
                  <th>Estado</th>
                  <th>Reintentos</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((msg) => (
                  <tr key={msg.id}>
                    <td>
                      <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        {msg.number || 'N/A'}
                      </Typography>
                    </td>
                    <td style={{ maxWidth: 200 }}>
                      <JoyTooltip title={msg.message || ''}>
                        <Typography level="body-sm" sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {msg.message?.substring(0, 50) || '(Sin mensaje)'}
                          {msg.message && msg.message.length > 50 ? '...' : ''}
                        </Typography>
                      </JoyTooltip>
                    </td>
                    <td style={{ maxWidth: 200 }}>
                      <JoyTooltip title={msg.error || ''}>
                        <Typography level="body-sm" sx={{
                          color: 'danger.main',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {msg.error?.substring(0, 40) || 'Error desconocido'}
                          {msg.error && msg.error.length > 40 ? '...' : ''}
                        </Typography>
                      </JoyTooltip>
                    </td>
                    <td>
                      <Chip size="sm" variant="soft">
                        {msg.endpoint}
                      </Chip>
                    </td>
                    <td>
                      <Chip
                        size="sm"
                        color={getStatusColor(msg.status) as any}
                      >
                        {msg.status === 'pending' ? 'Pendiente' :
                         msg.status === 'retried' ? 'Reenviado' : 'Fallido'}
                      </Chip>
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {msg.retryCount}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm" sx={{ fontSize: '0.75rem' }}>
                        {formatDate(msg.createdAt)}
                      </Typography>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <JoyTooltip title="Ver detalles">
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="primary"
                            onClick={() => openDetails(msg)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </JoyTooltip>
                        {(msg.status === 'pending' || msg.status === 'failed') && (
                          <JoyTooltip title="Reintentar">
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="success"
                              onClick={() => handleRetry(msg.id)}
                              disabled={retryingId === msg.id}
                            >
                              {retryingId === msg.id ? (
                                <CircularProgress size="sm" />
                              ) : (
                                <RefreshIcon fontSize="small" />
                              )}
                            </IconButton>
                          </JoyTooltip>
                        )}
                        <JoyTooltip title="Eliminar">
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => handleDelete(msg.id)}
                            disabled={deletingId === msg.id}
                          >
                            {deletingId === msg.id ? (
                              <CircularProgress size="sm" />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </JoyTooltip>
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Sheet>

          {failedPagination && failedPagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Página {failedPagination.page} de {failedPagination.pages} · {failedPagination.total} registros
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="sm"
                  variant="outlined"
                  disabled={failedPage <= 1 || failedLoading}
                  onClick={() => setFailedPage(page => Math.max(1, page - 1))}
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  variant="outlined"
                  disabled={failedPagination.page >= failedPagination.pages || failedLoading}
                  onClick={() => setFailedPage(page => page + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Modal de detalles */}
      <Modal open={detailsOpen} onClose={() => setDetailsOpen(false)}>
        <ModalDialog>
          <DialogTitle>
            <WarningIcon sx={{ color: 'warning.main', mr: 1 }} />
            Detalles del Mensaje Fallido
          </DialogTitle>
          <DialogContent>
            {selectedFailed && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Número
                  </Typography>
                  <Typography level="body-md">
                    {selectedFailed.number || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Mensaje
                  </Typography>
                  <Sheet variant="soft" sx={{ p: 1, borderRadius: 'sm', maxHeight: 100, overflow: 'auto' }}>
                    <Typography level="body-sm">
                      {selectedFailed.message || '(Sin mensaje)'}
                    </Typography>
                  </Sheet>
                </Box>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Error
                  </Typography>
                  <Sheet variant="soft" color="danger" sx={{ p: 1, borderRadius: 'sm', maxHeight: 100, overflow: 'auto' }}>
                    <Typography level="body-sm" sx={{ color: 'danger.main' }}>
                      {selectedFailed.error || 'Error desconocido'}
                    </Typography>
                  </Sheet>
                </Box>
                <Grid container spacing={2}>
                  <Grid xs={6}>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      Código de Error
                    </Typography>
                    <Typography level="body-md">
                      {selectedFailed.errorCode || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      Subcódigo
                    </Typography>
                    <Typography level="body-md">
                      {selectedFailed.errorSubcode || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    FBTrace ID
                  </Typography>
                  <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                    {selectedFailed.fbtraceId || 'N/A'}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid xs={6}>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      Endpoint
                    </Typography>
                    <Chip size="sm">
                      {selectedFailed.endpoint}
                    </Chip>
                  </Grid>
                  <Grid xs={6}>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      Estado
                    </Typography>
                    <Chip
                      size="sm"
                      color={getStatusColor(selectedFailed.status) as any}
                    >
                      {selectedFailed.status}
                    </Chip>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid xs={6}>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      Reintentos
                    </Typography>
                    <Typography level="body-md">
                      {selectedFailed.retryCount}
                    </Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      Ticket ID
                    </Typography>
                    <Typography level="body-md">
                      {selectedFailed.ticketId || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Metadata
                  </Typography>
                  <Sheet variant="soft" sx={{ p: 1, borderRadius: 'sm', maxHeight: 150, overflow: 'auto' }}>
                    <Typography level="body-sm" component="pre" sx={{ fontSize: '0.75rem', m: 0 }}>
                      {JSON.stringify(selectedFailed.metadata, null, 2) || 'N/A'}
                    </Typography>
                  </Sheet>
                </Box>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Fecha de Creación
                  </Typography>
                  <Typography level="body-md">
                    {formatDate(selectedFailed.createdAt)}
                  </Typography>
                </Box>
              </Stack>
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Stack>
  )
  }

  return (
    <Container maxWidth="xl">
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <ApiIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography level="h2">API de Mensajes</Typography>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Integracion API para envio de mensajes
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Tabs */}
        <Tabs value={tabIndex} onChange={(_, value) => setTabIndex(value as number)}>
          <TabList>
            <Tab>
              <DashboardIcon sx={{ mr: 1 }} />
              Dashboard
            </Tab>
            <Tab>
              <ErrorIcon sx={{ mr: 1 }} />
              Mensajes Fallidos
            </Tab>
            <Tab>
              <AutoAwesomeIcon sx={{ mr: 1 }} />
              IA e Intervalo
            </Tab>
            <Tab>
              <MenuBookIcon sx={{ mr: 1 }} />
              Guia de uso
            </Tab>
          </TabList>

          <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
            <DashboardContent />
          </TabPanel>

          <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
            <FailedMessagesContent />
          </TabPanel>

          <TabPanel value={2} sx={{ p: 0, pt: 2 }}>
            <ApiMessageSettingsPanel />
          </TabPanel>

          <TabPanel value={3} sx={{ p: 0, pt: 2 }}>
            <ApiMessageGuide />
          </TabPanel>
        </Tabs>
      </Stack>
    </Container>
  )
}
